import React, { forwardRef } from 'react';

interface ActionButtonProps {
  label: string;
  type?: 'button' | 'submit' | 'reset';
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Visible label while loading. Use an empty string for an icon-only loading state. */
  loadingLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outlined' | 'contained' | 'text' | 'icon';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'inherit' | 'white';
  /** Legacy: the canonical button is 14px at every size. */
  textSize?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | '8xl'
    | '9xl'
    | '10xl';
  fullWidth?: boolean;
  tooltip?: string | undefined;
  /**
   * Id of the form this button submits, for the case where the button is not
   * inside it — a dialog whose actions sit on the shell, outside the card.
   */
  form?: string;
  /**
   * Accessible name, for when the visible label is not specific enough — a
   * screen with four cards each showing an `Edit` button, say.
   */
  ariaLabel?: string | undefined;
}

// Ported from SP's own ActionButton so the two apps' CTAs are the same button.
//
// Every combination carries its own hover, active, focus-visible and disabled
// state. CP used to paint one blanket `hover:opacity-90` over all of them,
// which fades the border and the label together instead of tinting the
// background — the reason Reject Estimate never read like Reject Timesheet.
const ring = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
const disabledState = 'disabled:cursor-not-allowed disabled:opacity-50';

const getColorsByType = (
  color: ActionButtonProps['color'],
  variant: ActionButtonProps['variant']
) => {
  switch (color) {
    case 'error':
      if (variant === 'contained')
        return {
          icon: 'text-white',
          button: `text-white bg-ds-error hover:bg-[#A0121A] active:bg-[#8A1018] ${ring} focus-visible:ring-ds-error ${disabledState} disabled:hover:bg-ds-error`,
        };
      if (variant === 'outlined')
        return {
          icon: 'text-ds-error',
          button: `text-ds-error border border-ds-error bg-white hover:bg-ds-error-bg active:bg-[#F5DCE0] ${ring} focus-visible:ring-ds-error ${disabledState} disabled:hover:bg-white`,
        };
      return {
        icon: 'text-ds-error',
        button: `text-ds-error bg-transparent hover:bg-ds-error-bg active:bg-[#F5DCE0] ${ring} focus-visible:ring-ds-error ${disabledState} disabled:hover:bg-transparent`,
      };

    case 'warning':
      if (variant === 'contained')
        return {
          icon: 'text-white',
          button: `text-white bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800 ${ring} focus-visible:ring-yellow-500 ${disabledState}`,
        };
      if (variant === 'outlined')
        return {
          icon: 'text-yellow-600',
          button: `text-yellow-600 border border-yellow-600 bg-white hover:bg-yellow-50 active:bg-yellow-100 ${ring} focus-visible:ring-yellow-500 ${disabledState}`,
        };
      return {
        icon: 'text-yellow-600',
        button: `text-yellow-600 bg-transparent hover:bg-yellow-50 active:bg-yellow-100 ${ring} focus-visible:ring-yellow-500 ${disabledState}`,
      };

    case 'info':
    case 'primary':
      if (variant === 'contained')
        return {
          icon: 'text-white',
          button: `text-white bg-primary hover:bg-primary-hover active:bg-[#2A2D8A] ${ring} focus-visible:ring-primary ${disabledState} disabled:hover:bg-primary`,
        };
      if (variant === 'outlined')
        return {
          icon: 'text-primary',
          button: `text-primary border border-primary bg-white hover:bg-ds-info-bg active:bg-ds-accent-blue-bg ${ring} focus-visible:ring-primary ${disabledState} disabled:hover:bg-white`,
        };
      return {
        icon: 'text-primary',
        button: `text-primary bg-transparent hover:bg-ds-info-bg hover:underline active:bg-ds-accent-blue-bg ${ring} focus-visible:ring-primary ${disabledState} disabled:hover:bg-transparent disabled:no-underline`,
      };

    case 'white':
      if (variant === 'contained')
        return {
          icon: 'text-ds-text-primary',
          button: `text-ds-text-primary bg-white hover:bg-ds-bg-secondary active:bg-ds-bg-subtle border border-ds-border ${ring} focus-visible:ring-ds-border ${disabledState}`,
        };
      return {
        icon: 'text-white',
        button: `text-white border border-white bg-transparent hover:bg-white/10 ${ring} focus-visible:ring-white ${disabledState}`,
      };

    case 'secondary':
      if (variant === 'contained')
        return {
          icon: 'text-ds-text-primary',
          button: `text-ds-text-primary bg-ds-bg-secondary hover:bg-ds-bg-subtle active:bg-ds-border ${ring} focus-visible:ring-ds-border ${disabledState}`,
        };
      if (variant === 'outlined')
        return {
          icon: 'text-ds-text-secondary',
          button: `text-ds-text-secondary border border-ds-border bg-white hover:bg-ds-bg-secondary active:bg-ds-bg-subtle ${ring} focus-visible:ring-ds-border ${disabledState} disabled:hover:bg-white`,
        };
      return {
        icon: 'text-ds-text-secondary',
        button: `text-ds-text-secondary bg-transparent hover:bg-ds-bg-secondary active:bg-ds-bg-subtle ${ring} focus-visible:ring-ds-border ${disabledState}`,
      };

    default:
      // inherit / unknown — also the `icon` variant's square.
      return {
        icon: 'text-ds-text-secondary',
        button: `text-ds-text-primary bg-white border border-ds-border hover:bg-ds-bg-secondary active:bg-ds-bg-subtle ${ring} focus-visible:ring-ds-border ${disabledState} disabled:hover:bg-white`,
      };
  }
};

const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      label,
      type = 'button',
      icon,
      onClick,
      className = '',
      disabled = false,
      loading = false,
      loadingLabel,
      size = 'md',
      variant = 'contained',
      color = 'inherit',
      textSize: _textSize = 'sm',
      fullWidth = false,
      tooltip,
      form,
      ariaLabel,
    },
    ref
  ) => {
    const disableBtn = disabled || loading;
    const colors = getColorsByType(color, variant);

    // Figma spec — two heights for CTAs:
    //   sm/md (32px, radius 10px) -> secondary CTAs (section headers, modals)
    //   lg    (40px, radius 12px) -> primary CTAs (list-view page headers)
    const sizeClasses =
      variant === 'icon'
        ? 'w-8 h-8 rounded-[10px]'
        : size === 'sm'
          ? 'h-8 px-3 text-[14px] gap-1.5 tracking-[-0.02em] leading-[1.4] rounded-[10px]'
          : size === 'lg'
            ? 'h-10 px-4 text-[14px] gap-2 tracking-[-0.02em] leading-[1.4] rounded-[12px]'
            : 'h-8 px-4 text-[14px] gap-1.5 tracking-[-0.02em] leading-[1.4] rounded-[10px]';

    const spinner = (
      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const buttonElement = (
      <button
        ref={ref}
        disabled={disableBtn}
        type={type}
        {...(form ? { form } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        onClick={() => {
          if (!disableBtn) {
            const button = document.activeElement;
            setTimeout(() => {
              if (button instanceof HTMLElement) {
                button.blur();
              }
            }, 50);
            if (onClick) onClick();
          }
        }}
        className={`inline-flex items-center justify-center whitespace-nowrap font-semibold transition-colors ${sizeClasses} ${colors.button} ${
          fullWidth ? 'w-full' : ''
        } ${className}`}
      >
        {loading ? (
          <>
            {spinner}
            {loadingLabel ?? label}
          </>
        ) : (
          <>
            {icon && <span className="inline-flex items-center">{icon}</span>}
            {label}
          </>
        )}
      </button>
    );

    if (tooltip) {
      return (
        <div className="relative group">
          {buttonElement}
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-black/80 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
              {tooltip}
            </div>
          </div>
        </div>
      );
    }

    return buttonElement;
  }
);

ActionButton.displayName = 'ActionButton';

export default ActionButton;
